import { Middleware } from "redux";
import { getType } from "typesafe-actions";
import { AuthActionTypes } from "./auth/types";
import { TourActionTypes } from "./tour/types";
import { HelpActionTypes } from "./help/types";
import * as helpActions from "./help/actions";
import { LocalStorageKeys } from "../utils/LocalStorageUtils";
import { ApplicationState } from "./index";

/**
 * Centralizes all `localStorage`/`document` side effects that previously
 * lived inside the auth/tour/help reducers. Reducers must stay pure
 * functions of (state, action) - this middleware performs those effects in
 * response to the same actions instead, running them either before or
 * after the reducers depending on whether the persisted value comes from
 * the action payload or from the resulting store state.
 */
export const persistenceMiddleware: Middleware<{}, ApplicationState> =
  (store) => (next) => (action) => {
    // Effects that only need the action payload can run before or after -
    // done here, before the reducers run.
    switch (action.type) {
      case AuthActionTypes.UPDATE_PROFILE_SUCCESS:
      case AuthActionTypes.LOAD_USER_INFO_SUCCESS:
      case AuthActionTypes.PATCH_PROFILE_SUCCESS:
      case AuthActionTypes.GROUP_REORDER_UPDATE_SUCCESS:
        localStorage.setItem(
          LocalStorageKeys.CurrentUser,
          JSON.stringify(action.payload.userInfo)
        );
        break;

      case AuthActionTypes.LOGOUT:
        localStorage.clear();
        sessionStorage.clear();
        break;

      case TourActionTypes.MARK_FIRST_UPLOAD_TOUR_AS_SHOWN:
        localStorage.setItem(LocalStorageKeys.FirstUploadTour, JSON.stringify(true));
        break;
      case TourActionTypes.MARK_SIDEBAR_POSITION_TOUR_AS_SHOWN:
        localStorage.setItem(
          LocalStorageKeys.SidebarPositionTour,
          JSON.stringify(true)
        );
        break;
      case TourActionTypes.MARK_PREPARE_MEETING_TOUR_AS_SHOWN:
        localStorage.setItem(
          LocalStorageKeys.PrepareMeetingTour,
          JSON.stringify(true)
        );
        break;
      case TourActionTypes.MARK_IMPLEMENT_MEETING_TOUR_AS_SHOWN:
        localStorage.setItem(
          LocalStorageKeys.ImplementMeetingTour,
          JSON.stringify(true)
        );
        break;
      case TourActionTypes.MARK_INTERVENTION_FIDELITY_TOUR_AS_SHOWN:
        localStorage.setItem(
          LocalStorageKeys.InterventionFidelityTour,
          JSON.stringify(true)
        );
        break;
      case TourActionTypes.MARK_FIDELITY_CHART_TOUR_AS_SHOWN:
        localStorage.setItem(
          LocalStorageKeys.FidelityChartTour,
          JSON.stringify(true)
        );
        break;
      case TourActionTypes.MARK_EMPTY_DATA_PERIOD_VIEW_TOUR_AS_SHOWN:
        localStorage.setItem(
          LocalStorageKeys.EmptyDataPeriodViewTour,
          JSON.stringify(true)
        );
        break;

      case getType(helpActions.changeLocale):
        localStorage.setItem(LocalStorageKeys.Locale, action.payload);
        break;

      default:
        break;
    }

    const result = next(action);

    // These two depend on state computed *inside* the reducer from the
    // previous state (the toggled `showTopBar` value, and the merged
    // `userInfo`), so the effect must run after the reducer has produced
    // the new state.
    switch (action.type) {
      case AuthActionTypes.UPDATE_DO_NOT_SHOW_SUCCESS: {
        const { userInfo } = store.getState().auth;
        localStorage.setItem(LocalStorageKeys.CurrentUser, JSON.stringify(userInfo));
        break;
      }

      case HelpActionTypes.CHANGE_DISPLAYED_BAR: {
        const { showTopBar } = store.getState().help;
        localStorage.setItem(LocalStorageKeys.ShowTopBar, JSON.stringify(showTopBar));

        if (typeof document !== "undefined") {
          document.body.classList.toggle("sidebar-hidden", !!showTopBar);
        }
        break;
      }

      default:
        break;
    }

    return result;
  };

export default persistenceMiddleware;
